import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import PasswordStrengthIndicator from '../../auth/PasswordStrengthIndicator';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PASSWORD_MIN_LENGTH, PASSWORD_REGEX } from '../../../utils/validation/auth.schema';

// Create a schema for the unlink Google form
const unlinkGoogleSchema = z
    .object({
        newPassword: z
            .string()
            .min(PASSWORD_MIN_LENGTH, { message: 'auth.validation.passwordTooShort' })
            .regex(PASSWORD_REGEX, { message: 'auth.validation.passwordComplexity' }),
        confirmPassword: z.string().min(1, { message: 'auth.validation.required' }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: 'auth.validation.passwordsDontMatch',
        path: ['confirmPassword'],
    });

type UnlinkGoogleFormData = z.infer<typeof unlinkGoogleSchema>;

interface UnlinkGoogleDialogProps {
    open: boolean;
    isLoading: boolean;
    error: string;
    newPassword: string;
    confirmPassword: string;
    onClose: () => void;
    onConfirm: () => void;
    onNewPasswordChange: (value: string) => void;
    onConfirmPasswordChange: (value: string) => void;
}

const UnlinkGoogleDialog: React.FC<UnlinkGoogleDialogProps> = ({
    open,
    isLoading,
    error,
    newPassword,
    confirmPassword,
    onClose,
    onConfirm,
    onNewPasswordChange,
    onConfirmPasswordChange,
}) => {
    const { t } = useTranslation();
    
    const {
        control,
        handleSubmit,
        watch,
        formState: { isValid, isDirty, touchedFields },
        trigger,
        reset,
    } = useForm<UnlinkGoogleFormData>({
        resolver: zodResolver(unlinkGoogleSchema),
        defaultValues: {
            newPassword: newPassword || '',
            confirmPassword: confirmPassword || '',
        },
        mode: 'onBlur',
    });

    // Watch password for strength indicator
    const watchedPassword = watch('newPassword');
    const watchedConfirmPassword = watch('confirmPassword');
    
    // Update parent component state when form values change
    useEffect(() => {
        if (watchedPassword !== undefined) {
            onNewPasswordChange(watchedPassword);
        }
    }, [watchedPassword, onNewPasswordChange]);
    
    useEffect(() => {
        if (watchedConfirmPassword !== undefined) {
            onConfirmPasswordChange(watchedConfirmPassword);
        }
    }, [watchedConfirmPassword, onConfirmPasswordChange]);
    
    // Only trigger validation after user has interacted with fields
    useEffect(() => {
        if (touchedFields.newPassword || touchedFields.confirmPassword) {
            trigger();
        }
    }, [watchedPassword, watchedConfirmPassword, trigger, touchedFields]);
    
    // Validate password match only when confirmPassword has been touched
    useEffect(() => {
        if (watchedConfirmPassword && touchedFields.confirmPassword) {
            trigger('confirmPassword');
        }
    }, [watchedConfirmPassword, watchedPassword, trigger, touchedFields]);
    
    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open, reset]);
    
    const onSubmit = () => {
        onConfirm();
    };
    
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)'
                }
            }}
        >
            <DialogTitle>{t('settings.dialogs.unlinkGoogle.title')}</DialogTitle>
            <DialogContent>
                <Stack sx={{ 
                    mt: 1, 
                    gap: 2 
                }}>
                    <Alert severity="warning" sx={{ 
                        mb: 1 
                    }}>
                        {t('settings.dialogs.unlinkGoogle.description')}
                    </Alert>
                    
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Controller
                            name="newPassword"
                            control={control}
                            render={({ field, fieldState }) => (
                                <>
                                    <TextField
                                        {...field}
                                        type="password"
                                        label={t('settings.dialogs.unlinkGoogle.newPassword')}
                                        fullWidth
                                        error={!!fieldState.error}
                                        helperText={fieldState.error ? t(fieldState.error.message as string) : ''}
                                        sx={{ mb: 1 }}
                                    />
                                    <PasswordStrengthIndicator password={field.value} />
                                </>
                            )}
                        />
                        
                        <Controller
                            name="confirmPassword"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    type="password"
                                    label={t('settings.dialogs.unlinkGoogle.confirmPassword')}
                                    fullWidth
                                    error={!!fieldState.error || !!error}
                                    helperText={
                                        fieldState.error 
                                            ? t(fieldState.error.message as string) 
                                            : error
                                    }
                                />
                            )}
                        />
                    </form>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                     {t('settings.dialogs.unlinkGoogle.buttons.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    color="primary"
                    disabled={!isValid || !isDirty || isLoading}
                >
                    {isLoading ? <CircularProgress size={24} /> : t('settings.dialogs.unlinkGoogle.buttons.unlink')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UnlinkGoogleDialog;
